import { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import type { VaultItem } from '@heybesafe/shared';
import { useSafe } from '../../context/SafeContext';
import LoadingPane from '../../components/LoadingPane';
import { useFamilyCategories } from '../../hooks/useFamilyCategories';
import { HbsButton } from '../../components/hbs/Button';
import { Icon } from '../../components/hbs/Icon';
import { describeRecurrence, formatDate, nextOccurrence } from '../../binder/recurrence';

/** Local `YYYY-MM-DD` for a Date, so `formatDate` (which is timezone-safe on
 *  date strings) can render a computed next-due Date consistently. */
function toISODate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Read-only family schedule detail: the recurrence summary, start date, next
 * due, mark-done state, and an optional link to the related Binder item. A
 * "Mark done" action stamps `lastCompletedAt`, which rolls the next-due forward.
 */
export default function ScheduleDetailPage(): JSX.Element {
  const { orgId, categoryId, itemId } = useParams<{ orgId: string; categoryId: string; itemId: string }>();
  const navigate = useNavigate();
  const { items, itemsLoaded, orgs = [], updateItem, deleteItem, sync } = useSafe();
  const categoriesByOrg = useFamilyCategories();
  const [marking, setMarking] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const item = items.find((i) => i.id === itemId && i.orgId === orgId && i.type === 'schedule');
  const org = orgs.find((o) => o.id === orgId);
  // Deleting a shared item is admin-only (the server rejects a member's
  // tombstone), so only offer Delete to admins.
  const isAdmin = org?.role === 'admin';
  const category = (orgId ? categoriesByOrg.get(orgId) ?? [] : []).find((c) => c.categoryId === (item?.categoryId ?? categoryId));
  const backTo = `/orgs/${orgId}/binder/${categoryId}`;

  if (!item) {
    // items is emptied on lock and only repopulated by the post-unlock
    // syncPull, so an absent item means loading until itemsLoaded.
    if (!itemsLoaded) return <LoadingPane label="Loading schedule…" />;
    return (
      <div className="h-full flex flex-col items-center justify-center text-hbs-text-mute gap-3">
        <p className="text-[14px]">Schedule not found.</p>
        <HbsButton id="schedule-not-found-back-button" variant="plain" onClick={() => navigate(backTo)}>Back</HbsButton>
      </div>
    );
  }

  const next = nextOccurrence(item.rrule, item.dtstart, item.lastCompletedAt);
  const related = item.relatedItemId
    ? items.find((i) => i.id === item.relatedItemId && i.orgId === orgId)
    : undefined;
  const relatedHref = relatedItemHref(related, orgId!, categoryId);

  async function markDone(): Promise<void> {
    if (!item) return;
    setMarking(true);
    try {
      await updateItem(item.id, { type: 'schedule', lastCompletedAt: new Date().toISOString() });
      await sync();
    } finally {
      setMarking(false);
    }
  }

  async function handleDelete(): Promise<void> {
    if (!item) return;
    setDeleting(true);
    try {
      await deleteItem(item.id);
      await sync();
      navigate(backTo);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="p-7 flex flex-col gap-6 max-w-3xl">
      <div>
        <p className="text-[12px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute">
          {org?.name ?? 'Family'}{category ? ` · ${category.name}` : ''}
        </p>
        <h1 className="font-display font-semibold text-[30px] tracking-hbs-tighter text-hbs-text">
          {item.name}
        </h1>
        <div className="flex items-center gap-4">
          <Link
            to={`/orgs/${orgId}/binder/${categoryId}/schedules/${item.id}/edit`}
            className="text-[13px] font-semibold text-hbs-primary hover:underline"
          >
            Edit
          </Link>
          {isAdmin && !confirming && (
            <button
              id="schedule-delete-button"
              type="button"
              onClick={() => setConfirming(true)}
              className="text-[13px] font-semibold text-hbs-warning hover:underline"
            >
              Delete
            </button>
          )}
          {isAdmin && confirming && (
            <span className="inline-flex items-center gap-2 text-[13px]">
              <span className="text-hbs-text-mute">Delete this schedule?</span>
              <button
                id="schedule-delete-cancel-button"
                type="button"
                onClick={() => setConfirming(false)}
                className="font-semibold text-hbs-text hover:underline"
              >
                Cancel
              </button>
              <button
                id="schedule-delete-confirm-button"
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="font-semibold text-hbs-warning hover:underline disabled:opacity-60"
              >
                {deleting ? 'Deleting…' : 'Confirm delete'}
              </button>
            </span>
          )}
        </div>
      </div>

      {/* Schedule details. */}
      <section className="bg-hbs-surface border border-hbs-border rounded-hbs-xl p-6 flex flex-col gap-4">
        <Field label="Repeats" value={describeRecurrence(item.rrule)} />
        {formatDate(item.dtstart) && <Field label="Starts" value={formatDate(item.dtstart)!} />}
        {next && <Field label="Next due" value={formatDate(toISODate(next))!} />}
        <Field label="Last completed" value={formatDate(item.lastCompletedAt) ?? 'Never'} />

        <div>
          <HbsButton id="schedule-mark-done-button" type="button" onClick={markDone} loading={marking}>
            <Icon name="check" size={14} /> Mark done
          </HbsButton>
        </div>
      </section>

      {/* Related Binder item — its own box, mirroring the document detail. */}
      {related && (
        <section className="bg-hbs-surface border border-hbs-border rounded-hbs-xl p-6 flex flex-col gap-2">
          <div className="text-[12px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute">Related</div>
          {relatedHref ? (
            <Link to={relatedHref} className="inline-flex items-center gap-2 text-[14px] text-hbs-primary hover:underline">
              <Icon name={related.type === 'contact' ? 'contact' : 'note'} size={14} /> {related.name}
            </Link>
          ) : (
            <span className="inline-flex items-center gap-2 text-[14px] text-hbs-text">{related.name}</span>
          )}
        </section>
      )}

      <div>
        <HbsButton id="schedule-back-to-category-button" variant="plain" onClick={() => navigate(backTo)}>Back to {category?.name ?? 'category'}</HbsButton>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <div className="text-[12px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute">{label}</div>
      <div className="text-[15px] text-hbs-text">{value}</div>
    </div>
  );
}

/** Detail-page href for a schedule's related contact/document, mirroring
 *  LinkedItemsSection's conventions. Schedules/other types → no link. */
function relatedItemHref(item: VaultItem | undefined, orgId: string, categoryId?: string): string | undefined {
  if (!item) return undefined;
  if (item.type === 'contact') {
    return item.categoryId
      ? `/orgs/${orgId}/binder/${item.categoryId}/contacts/${item.id}`
      : `/orgs/${orgId}/binder/contacts/${item.id}`;
  }
  if (item.type === 'document') {
    return `/orgs/${orgId}/binder/${item.categoryId ?? categoryId}/documents/${item.id}`;
  }
  return undefined;
}
